// Released revisions of a spec: the history (the usual table, DataTable; a row shows that revision as it was released
// below it), compare any two revisions or a revision and the draft (text diff plus file changes), and the admin's
// Release dialog.
import { diffLines, diffWordsWithSpace } from "diff";
import { ArrowRight, Download, GitCompare } from "lucide-react";
import { type ReactNode, useMemo, useState } from "react";
import type { Bom, FileVersion, Spec, SpecRevision } from "../../../../shared/db.gen.ts";
import { type FileChanges, type FileRef, fileChanges, revNumber } from "../../../../shared/specs.ts";
import { bomLink, useBomIndex } from "../../lib/boms.ts";
import { mutateWithResult } from "../../lib/mutate.ts";
import { Link } from "../../lib/router.tsx";
import { useRows } from "../../lib/sync.ts";
import { Badge, Button, cx, Select, Textarea } from "../../ui/controls.tsx";
import { type Column, DataTable } from "../../ui/DataTable.tsx";
import { DialogLayer } from "../../ui/DialogLayer.tsx";
import { toast } from "../../ui/toast.tsx";
import { bytes, day, downloadUrl, draftRefs, releasePreview, type SpecIndex, statusOf } from "./lib.ts";
import { MarkdownPreview } from "./MarkdownEditor.tsx";

const DRAFT = "draft";
const OPEN_PO = new Set(["sent", "acknowledged", "in_production", "shipped"]);

/** Text and files on one side of a comparison: a revision, or the draft as saved. */
function side(ix: SpecIndex, spec: Spec, key: string) {
  if (key === DRAFT) return { label: "Draft", md: spec.draft_md, zh: spec.draft_md_zh, files: draftRefs(ix, spec) };
  const r = ix.revisionById.get(key);
  return { label: r ? `Rev ${r.rev}` : "?", md: r?.body_md ?? "", zh: r?.body_md_zh ?? "", files: ix.filesByRevision.get(key) ?? [] };
}

export function SpecRevisions({ spec, ix, wide }: { spec: Spec; ix: SpecIndex; wide?: boolean }) {
  const revs = ix.revisionsBySpec.get(spec.id) ?? [];
  const status = statusOf(ix, spec);
  const notices = useRows("change_notice");
  const noticeNumber = useMemo(() => new Map(notices.map((n) => [n.id, n.number])), [notices]);
  const current = spec.current_revision_id;
  const defaultCompare = () => {
    if (status.kind === "ahead" && current) return { from: current, to: DRAFT };
    if (revs.length >= 2) return { from: revs[revs.length - 2].id, to: revs[revs.length - 1].id };
    return null;
  };
  const [cmp, setCmp] = useState<{ from: string; to: string } | null>(defaultCompare);
  const [viewing, setViewing] = useState<string | null>(null);
  // One revision per item (server/src/item-release.ts): a sub-assembly's or a product's BOM is released with its spec
  // under one number, so its history shows both; a BOM released before the spec had one is a row of its own.
  const boms = useRows("bom");
  const bomRevs = useMemo(
    () =>
      boms.filter(
        (b) =>
          b.status !== "draft" && (spec.shopify_product_id ? b.shopify_product_id === spec.shopify_product_id : spec.item_id && b.item_id === spec.item_id),
      ),
    [boms, spec.item_id, spec.shopify_product_id],
  );
  const bomOf = new Map(bomRevs.map((b) => [b.revision, b]));
  const specNumbers = new Set(revs.map((r) => revNumber(r.rev)));
  const rows: RevLine[] = [
    ...revs.map((r, i) => ({ id: r.id, n: revNumber(r.rev), r, previous: revs[i - 1] as typeof r | undefined, bom: bomOf.get(revNumber(r.rev)) })),
    ...bomRevs.filter((b) => !specNumbers.has(b.revision)).map((b) => ({ id: b.id, n: b.revision, r: null, previous: undefined, bom: b })),
  ];
  const [slot, setSlot] = useState<HTMLDivElement | null>(null); // Table views --- above the table

  if (!rows.length) return <div className="px-2 py-2 text-muted">Not released yet. Its first release is Rev 1.</div>;

  const by = (r: SpecRevision) => (r.released_by ? (ix.userName.get(r.released_by) ?? "a former user") : "HHL Ops");
  const columns: Column<RevLine>[] = [
    {
      key: "rev",
      header: "Rev",
      width: "80px",
      value: (l) => l.n,
      text: (l) => `Rev ${l.r?.rev ?? l.n}`,
      cell: (l) => <Badge tone={l.r && l.r.id === current ? "ok" : "neutral"}>Rev {l.r?.rev ?? l.n}</Badge>,
    },
    {
      key: "note",
      header: "Note",
      width: "minmax(200px,2fr)",
      value: (l) => (l.r ? l.r.change_note : l.bom?.change_note || "BOM released"),
      cell: (l) =>
        l.r ? (
          <span className="truncate" title={l.r.change_note || undefined}>
            {l.r.change_note}
          </span>
        ) : (
          <span className="truncate" title={l.bom?.change_note || undefined}>
            {l.bom?.change_note || "BOM released"}
            <span className="text-[12px] text-muted"> · the BOM alone (no spec then)</span>
          </span>
        ),
    },
    {
      key: "released",
      header: "Released",
      width: "130px",
      value: (l) => (l.r ? l.r.released_at : (l.bom?.effective_from ?? "")),
      cell: (l) => (
        <span className="truncate text-[12px] text-muted">
          {l.r ? day(l.r.released_at) : l.bom?.effective_from ? `In effect ${day(l.bom.effective_from)}` : ""}
        </span>
      ),
    },
    {
      key: "by",
      header: "By",
      width: "120px",
      value: (l) => (l.r ? by(l.r) : ""),
      cell: (l) => (l.r ? <span className="truncate text-[12px] text-muted">{by(l.r)}</span> : null),
    },
    {
      key: "files",
      header: "Files",
      width: "70px",
      align: "right",
      value: (l) => (l.r ? (ix.filesByRevision.get(l.r.id)?.length ?? 0) : null),
      cell: (l) => (l.r ? <span className="text-[12px] text-muted">{ix.filesByRevision.get(l.r.id)?.length ?? 0}</span> : null),
    },
    {
      key: "ecn",
      header: "ECN",
      headerTitle: "The change notice it was released with",
      width: "100px",
      value: (l) => (l.r?.change_notice_id ? (noticeNumber.get(l.r.change_notice_id) ?? "ECN") : ""),
      cell: (l) =>
        l.r?.change_notice_id ? (
          <Link
            to={`/specs?tab=ecn&open=${l.r.change_notice_id}`}
            className="text-[12px] text-accent-text hover:underline"
            onClick={(e) => e.stopPropagation()}
          >
            {noticeNumber.get(l.r.change_notice_id) ?? "ECN"}
          </Link>
        ) : null,
    },
    {
      key: "bom",
      header: "BOM",
      headerTitle: "The BOM released under the same number",
      width: "130px",
      value: (l) => (l.bom ? `BOM Rev ${l.bom.revision}${l.bom.status === "retired" ? " (retired)" : ""}` : ""),
      cell: (l) =>
        l.bom ? (
          <Link to={bomLink(l.bom.id)} className="truncate text-[12px] text-accent-text hover:underline" onClick={(e) => e.stopPropagation()}>
            BOM Rev {l.bom.revision}
            {l.bom.status === "retired" ? " (retired)" : ""}
          </Link>
        ) : null,
    },
    {
      key: "compare",
      header: "",
      width: "90px",
      value: () => null,
      cell: (l) => {
        const { r, previous } = l;
        return r && previous ? (
          <Button
            size="sm"
            variant="ghost"
            icon={<GitCompare className="size-3.5" />}
            onClick={(e) => {
              e.stopPropagation();
              setCmp({ from: previous.id, to: r.id });
            }}
          >
            vs {previous.rev}
          </Button>
        ) : null;
      },
    },
  ];

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-col gap-1">
        <div className="flex items-center gap-2">
          {status.kind === "ahead" && current && (
            <Button size="sm" variant="ghost" icon={<GitCompare className="size-3.5" />} onClick={() => setCmp({ from: current, to: DRAFT })}>
              Draft vs Rev {status.rev}
            </Button>
          )}
          <div ref={setSlot} className="ml-auto flex items-center gap-1" />
        </div>
        <DataTable
          fit
          rows={rows}
          columns={columns}
          defaultSort={{ key: "rev", desc: true }}
          activeId={viewing}
          // A revision shows below the table as it was released (again to hide it); the BOM alone has nothing to show.
          onOpen={(l) => l.r && setViewing(viewing === l.r.id ? null : l.r.id)}
          rowNoun={["revision", "revisions"]}
          exportName="Spec revisions"
          viewsSlot={slot}
          pageFilters={false}
        />
        {viewing && <RevisionView ix={ix} revisionId={viewing} current={current} />}
      </div>
      {cmp && <Compare spec={spec} ix={ix} from={cmp.from} to={cmp.to} onChange={setCmp} wide={wide} />}
    </div>
  );
}

type RevLine = { id: string; n: number; r: SpecRevision | null; previous: SpecRevision | undefined; bom: Bom | undefined };

/** A revision as it was released: its files, then its text (English or 中文). */
function RevisionView({ ix, revisionId, current }: { ix: SpecIndex; revisionId: string; current: string | null }) {
  const r = ix.revisionById.get(revisionId);
  const [zh, setZh] = useState(false);
  const [slot, setSlot] = useState<HTMLDivElement | null>(null); // Table views --- the files table's menus
  if (!r) return null;
  const refs = ix.filesByRevision.get(r.id) ?? [];
  return (
    <div className="flex flex-col gap-2 rounded-md bg-surface-2 px-2 pt-2 pb-3">
      <div className="flex items-center gap-2">
        <Badge tone={r.id === current ? "ok" : "neutral"}>Rev {r.rev}</Badge>
        <span className="text-[11px] font-medium tracking-wide text-faint uppercase">Files</span>
        {refs.length > 0 && <div ref={setSlot} className="ml-auto flex items-center gap-1" />}
      </div>
      <FileList refs={refs} ix={ix} viewsSlot={slot} />
      {r.body_md_zh && (
        <div className="flex gap-1 text-[12px]">
          {[false, true].map((z) => (
            <button
              key={String(z)}
              type="button"
              onClick={() => setZh(z)}
              className={cx("rounded px-2 py-0.5", zh === z ? "bg-accent-soft text-accent-text" : "text-muted")}
            >
              {z ? "中文" : "English"}
            </button>
          ))}
        </div>
      )}
      <div className="max-h-[480px] overflow-y-auto rounded-md border border-line bg-surface px-4 py-3">
        <MarkdownPreview source={zh ? r.body_md_zh : r.body_md} />
      </div>
    </div>
  );
}

type RevFile = FileRef & { id: string; v: FileVersion | undefined; position: number };

/** A revision's files, with the labels it froze: the usual table. */
function FileList({ refs, ix, viewsSlot }: { refs: FileRef[]; ix: SpecIndex; viewsSlot: HTMLElement | null }) {
  if (!refs.length) return <div className="text-[12px] text-muted">No files in this revision.</div>;
  const rows: RevFile[] = refs.map((ref, i) => ({ ...ref, id: ref.file_version_id, v: ix.versionById.get(ref.file_version_id), position: i }));
  const columns: Column<RevFile>[] = [
    { key: "name", header: "File", width: "minmax(140px,1fr)", cell: (f) => <span className="truncate font-medium">{f.name}</span> },
    {
      key: "filename",
      header: "Filename",
      width: "minmax(160px,1.5fr)",
      value: (f) => f.v?.filename ?? "",
      cell: (f) => <span className="truncate font-mono text-[12px] text-muted">{f.v?.filename}</span>,
    },
    {
      key: "size",
      header: "Size",
      width: "80px",
      align: "right",
      value: (f) => f.v?.size ?? null,
      cell: (f) => <span className="text-[12px] text-muted">{f.v ? bytes(f.v.size) : ""}</span>,
    },
    {
      key: "download",
      header: "",
      width: "44px",
      value: () => null,
      cell: (f) =>
        f.v && (
          <a
            href={downloadUrl(f.v.id)}
            download={f.v.filename}
            onClick={(e) => e.stopPropagation()}
            className="rounded p-1 text-muted hover:bg-hover hover:text-text"
            title="Download"
          >
            <Download className="size-3.5" />
          </a>
        ),
    },
    // The revision's own order (never shown: what it's sorted by until someone sorts it another way).
    { key: "position", header: "", optional: true, width: "0px", value: (f) => f.position },
  ];
  return (
    <DataTable
      fit
      rows={rows}
      columns={columns}
      defaultSort={{ key: "position" }}
      rowNoun={["file", "files"]}
      exportName="Revision files"
      viewsSlot={viewsSlot}
      pageFilters={false}
    />
  );
}

function Compare({
  spec,
  ix,
  from,
  to,
  onChange,
  wide,
}: {
  spec: Spec;
  ix: SpecIndex;
  from: string;
  to: string;
  onChange: (c: { from: string; to: string } | null) => void;
  wide?: boolean;
}) {
  const revs = ix.revisionsBySpec.get(spec.id) ?? [];
  const options = [...revs.map((r) => ({ value: r.id, label: `Rev ${r.rev} · ${day(r.released_at)}` })).reverse(), { value: DRAFT, label: "Draft (as saved)" }];
  const a = side(ix, spec, from);
  const b = side(ix, spec, to);
  const [zh, setZh] = useState(false);
  const files = fileChanges(a.files, b.files);
  const changes = files.added.length + files.removed.length + files.changed.length;

  return (
    <div className="flex flex-col gap-2 rounded-md border border-line p-2">
      <div className="flex flex-wrap items-center gap-2">
        <GitCompare className="size-4 text-faint" />
        <div className={wide ? "w-56" : "w-40"}>
          <Select value={from} options={options} onChange={(e) => onChange({ from: e.target.value, to })} className="h-7 text-[12px]" />
        </div>
        <ArrowRight className="size-3.5 text-faint" />
        <div className={wide ? "w-56" : "w-40"}>
          <Select value={to} options={options} onChange={(e) => onChange({ from, to: e.target.value })} className="h-7 text-[12px]" />
        </div>
        {(a.zh || b.zh) && (
          <div className="flex rounded-md border border-line p-0.5 text-[12px]">
            {[false, true].map((z) => (
              <button
                key={String(z)}
                type="button"
                onClick={() => setZh(z)}
                className={cx("rounded px-2 py-0.5", zh === z ? "bg-accent-soft text-accent-text" : "text-muted")}
              >
                {z ? "中文" : "English"}
              </button>
            ))}
          </div>
        )}
        <Button size="sm" variant="ghost" className="ml-auto" onClick={() => onChange(null)}>
          Close
        </Button>
      </div>

      <div className="flex flex-col gap-0.5 text-[12px]">
        <div className="text-[11px] font-medium tracking-wide text-faint uppercase">Files</div>
        <FileChangeList changes={files} ix={ix} />
        <div className="text-muted">
          {changes ? `${files.same.length} unchanged` : files.same.length ? `No file changes (${files.same.length} the same)` : "No files on either side"}
        </div>
      </div>

      <div className="text-[11px] font-medium tracking-wide text-faint uppercase">Text</div>
      <TextDiff key={`${from}>${to}>${zh}`} before={zh ? a.zh : a.md} after={zh ? b.zh : b.md} />
    </div>
  );
}

/** Files added (+), replaced (~) and taken out (−), each with its label on that side and the file names. */
export function FileChangeList({ changes, ix }: { changes: FileChanges; ix: SpecIndex }) {
  const fileName = (ref: FileRef) => ix.versionById.get(ref.file_version_id)?.filename ?? "";
  return (
    <>
      {changes.added.map((f) => (
        <div key={f.file_version_id} className="text-ok">
          + {f.name} <span className="font-mono">{fileName(f)}</span>
        </div>
      ))}
      {changes.changed.map((c) => (
        <div key={c.to.file_version_id} className="text-warn">
          ~ {c.to.name}: <span className="font-mono">{fileName(c.from)}</span> → <span className="font-mono">{fileName(c.to)}</span>
        </div>
      ))}
      {changes.removed.map((f) => (
        <div key={f.file_version_id} className="text-danger">
          − {f.name} <span className="font-mono">{fileName(f)}</span>
        </div>
      ))}
    </>
  );
}

type DiffLine = { kind: "same" | "del" | "add"; parts: ReactNode[] };

/** Splits word-level changes into lines for one side of a changed block. */
function sideLines(changes: { value: string; added?: boolean; removed?: boolean }[], keep: "del" | "add"): ReactNode[][] {
  const lines: ReactNode[][] = [[]];
  let key = 0;
  for (const c of changes) {
    if (keep === "del" ? c.added : c.removed) continue;
    const changed = keep === "del" ? c.removed : c.added;
    c.value.split("\n").forEach((piece, j) => {
      if (j > 0) lines.push([]);
      if (!piece) return;
      key++;
      lines[lines.length - 1].push(
        changed ? (
          <mark key={key} className={cx(keep === "del" ? "bg-danger/25" : "bg-ok/25", "rounded-sm text-inherit")}>
            {piece}
          </mark>
        ) : (
          piece
        ),
      );
    });
  }
  if (lines.length > 1 && !lines[lines.length - 1].length) lines.pop();
  return lines;
}

function diffRows(before: string, after: string): DiffLine[] {
  const parts = diffLines(before, after);
  const out: DiffLine[] = [];
  const plain = (value: string) => value.replace(/\n$/, "").split("\n");
  for (let i = 0; i < parts.length; i++) {
    const p = parts[i];
    const next = parts[i + 1];
    if (p.removed && next?.added) {
      // A changed block: highlight the words that changed within its lines.
      const words = diffWordsWithSpace(p.value.replace(/\n$/, ""), next.value.replace(/\n$/, ""));
      for (const l of sideLines(words, "del")) out.push({ kind: "del", parts: l });
      for (const l of sideLines(words, "add")) out.push({ kind: "add", parts: l });
      i++;
    } else {
      for (const l of plain(p.value)) out.push({ kind: p.added ? "add" : p.removed ? "del" : "same", parts: [l] });
    }
  }
  return out;
}

const CONTEXT = 3;

export function TextDiff({ before, after }: { before: string; after: string }) {
  const rows = useMemo(() => diffRows(before, after), [before, after]);
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  if (before === after) return <div className="text-[12px] text-muted">No text changes.</div>;

  // Long unchanged runs fold to a few lines of context either side.
  const shown: (DiffLine | { fold: number; count: number })[] = [];
  for (let i = 0; i < rows.length; ) {
    if (rows[i].kind !== "same") {
      shown.push(rows[i++]);
      continue;
    }
    let j = i;
    while (j < rows.length && rows[j].kind === "same") j++;
    const run = rows.slice(i, j);
    const head = i === 0 ? 0 : CONTEXT;
    const tail = j === rows.length ? 0 : CONTEXT;
    if (run.length > head + tail + 2 && !expanded.has(i)) {
      shown.push(...run.slice(0, head), { fold: i, count: run.length - head - tail }, ...run.slice(run.length - tail));
    } else shown.push(...run);
    i = j;
  }

  return (
    <div className="overflow-x-auto rounded-md border border-line font-mono text-[12px] leading-5">
      {shown.map((r, i) =>
        "fold" in r ? (
          <button
            // biome-ignore lint/suspicious/noArrayIndexKey: folds are positional
            key={`fold${i}`}
            type="button"
            onClick={() => setExpanded(new Set(expanded).add(r.fold))}
            className="block w-full bg-surface-2 px-3 py-0.5 text-left text-faint hover:text-text"
          >
            ⋯ {r.count} unchanged lines
          </button>
        ) : (
          <div
            // biome-ignore lint/suspicious/noArrayIndexKey: diff lines are positional
            key={i}
            className={cx("flex px-1 whitespace-pre-wrap", r.kind === "del" && "bg-danger-soft", r.kind === "add" && "bg-ok-soft")}
          >
            <span className="w-4 shrink-0 text-faint select-none">{r.kind === "del" ? "−" : r.kind === "add" ? "+" : " "}</span>
            <span className="min-w-0 flex-1 break-words">{r.parts.length ? r.parts : " "}</span>
          </div>
        ),
      )}
    </div>
  );
}

/**
 * Release the draft as the item's next revision (admins). It shows the draft as it was when opened and releases exactly
 * that (the server checks its stamp); if the draft moves on meanwhile, it says so and shows the new one on request.
 * `text` is unsaved text, saved first (`beforeRelease`). One number per item (server/src/item-release.ts): a
 * sub-assembly's BOM draft goes with it, and the number is past its BOM's too. Only the first release goes without a note.
 */
export function ReleaseDialog({
  spec,
  ix,
  title,
  text,
  beforeRelease,
  onClose,
}: {
  spec: Spec;
  ix: SpecIndex;
  title: string;
  text?: { draft_md: string; draft_md_zh: string };
  beforeRelease: () => Promise<boolean>;
  onClose: () => void;
}) {
  const [shown, setShown] = useState(() => releasePreview(ix, spec, text));
  const moved = releasePreview(ix, spec, text).stamp !== shown.stamp;
  const { pending, missing } = shown;
  // The item's BOM (a sub-assembly's): its draft goes along, and the number is the item's next.
  const bix = useBomIndex();
  const boms = bix.families.get(spec.shopify_product_id ? `product:${spec.shopify_product_id}` : `item:${spec.item_id}`) ?? [];
  const bomDraft = boms.find((b) => b.status === "draft");
  const bomReleased = boms.filter((b) => b.status !== "draft");
  const rev = String(Math.max(Number(shown.rev) || 1, bomDraft?.revision ?? 0, Math.max(0, ...bomReleased.map((b) => b.revision)) + 1));
  const first = !shown.current && !bomReleased.length;
  const pos = useRows("po");
  const poLines = useRows("po_line");
  const openPos = useMemo(() => {
    const open = new Set(pos.filter((p) => OPEN_PO.has(p.status)).map((p) => p.id));
    return new Set(poLines.filter((l) => ix.specByItem.get(l.item_id)?.id === spec.id && l.spec_revision_id && open.has(l.po_id)).map((l) => l.po_id)).size;
  }, [pos, poLines, ix, spec.id]);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const nothing = (!pending.any || pending.empty) && !bomDraft;
  const files = pending.files.added.length + pending.files.changed.length + pending.files.same.length;

  const release = async () => {
    if (busy || moved) return;
    if (!note.trim() && !first) return toast.error("Say what changed in this revision");
    setBusy(true);
    if (await beforeRelease()) {
      const { ok } = await mutateWithResult("spec.release", { spec_id: spec.id, change_note: note, stamp: shown.stamp });
      if (ok) {
        toast.ok(`Released Rev ${rev}`);
        onClose();
      }
    }
    setBusy(false);
  };

  return (
    <DialogLayer z={50} onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          release();
        }}
        onKeyDown={(e) => {
          if (e.key === "Escape") {
            e.stopPropagation();
            onClose();
          }
          if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
            e.preventDefault();
            release();
          }
        }}
        className="w-[520px] rounded-xl border border-line bg-surface p-4 shadow-pop"
      >
        <div className="text-[15px] font-semibold">
          Release Rev {rev} <span className="font-normal text-muted">of {title}</span>
        </div>
        <div className="mt-1 text-[12px] text-muted">
          It freezes exactly the text and file versions below. Released revisions never change; the draft carries on.
        </div>

        <div className="mt-3 flex flex-col gap-0.5 rounded-md bg-surface-2 px-3 py-2 text-[12px]">
          <div>
            Text: {pending.text ? <span className="text-warn">changed{text && " (unsaved edits are saved first)"}</span> : "unchanged"}
            {pending.zh && <span className="text-warn"> · 中文 changed</span>}
          </div>
          <FileChangeList changes={pending.files} ix={ix} />
          <div className="text-muted">
            {files} {files === 1 ? "file" : "files"} in Rev {rev}
          </div>
          {bomDraft && (
            <div className="text-warn">
              BOM: its draft (lines, who supplies them, maker) is released with it{pending.any ? "" : "; the spec's text and files stay as they are"}
            </div>
          )}
          {missing.map((name) => (
            <div key={name} className="text-danger">
              “{name}” has nothing uploaded: upload it, or leave it out of the draft (Files).
            </div>
          ))}
          {nothing && <div className="text-danger">{shown.current ? `Nothing has changed since Rev ${shown.current}.` : "The draft is empty."}</div>}
          {openPos > 0 && shown.current && (
            <div className="text-muted">
              {openPos} open {openPos === 1 ? "PO was" : "POs were"} sent at an earlier revision; they'll show that Rev {rev} came out after.
            </div>
          )}
        </div>
        {moved && (
          <div className="mt-2 flex items-center gap-2 rounded-md bg-warn-soft px-3 py-2 text-[12px] text-warn">
            The draft changed while this was open (an upload or someone else's edit).
            <Button size="sm" variant="secondary" className="ml-auto" onClick={() => setShown(releasePreview(ix, spec, text))}>
              Show the new draft
            </Button>
          </div>
        )}

        <label className="mt-3 flex flex-col gap-1">
          <span className="text-[12px] text-muted">
            {first ? "Note (optional for the first release; vendors see it)" : "Change note (what changed and why; vendors see it)"}
          </span>
          <Textarea autoFocus value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Board thickness 1.6 → 1.2 mm for the new shell" />
        </label>
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" disabled={busy || moved || nothing || missing.length > 0 || (!note.trim() && !first)} kbd="Ctrl ↵">
            Release Rev {rev}
          </Button>
        </div>
      </form>
    </DialogLayer>
  );
}
